import immutableStore from 'alt-utils/lib/ImmutableUtil';
import {List, Map, Record} from 'immutable';
import alt from 'components/Dispatcher';
import PlaybackActions from 'actions/PlaybackActions';
import ShareSource from 'sources/ShareSource';
import CoachMeSource from 'sources/CoachMeSource';
import SharingActions from 'actions/sharing/SharingActions';
import SharingFormActions from 'actions/sharing/SharingFormActions';
import FileUploadSharingDialogFormStore from 'stores/sharing/FileUploadSharingDialogFormStore';
import classifyTargets from 'stores/sharing/classifyTargets';
import LibrarySource from 'sources/library/LibrarySource';
import ToastActions from '@store/toast/ToastActions';
import ToastStore from '@store/toast/ToastStore';

const State = Record({
  // permalink used to expand a specific call
  permalink: null,
  // Maps field names to an error string. Field-independent fields go to the '_' key.
  shareDataError: Map(),
  showSharingDialog: false,
  isSharingFileUploadLoading: false,
  loading: false,
  // the current fileupload that the user may be sharing
  fileUploadId: null,
});

/**
 * Stores the state of <DirectCallSharingDialog />
 */
@immutableStore
class FileUploadSharingStore {
  constructor() {
    this.state = State();
    this.bindActions(SharingActions);
    this.registerAsync(ShareSource);
    this.registerAsync(LibrarySource);
  }

  /** Close <DirectCallSharingDialog /> */
  onCloseSharingDialog() {
    const newState = this.state.set('showSharingDialog', false).set('shareDataError', Map());
    this.setState(newState);
    PlaybackActions.dialogMaskHidden.defer();
  }

  onSwitchTabs() {
    this.setState(this.state.set('shareDataError', Map()));
  }

  /** Open <DirectCallSharingDialog /> */
  onShowSharingDialog() {
    const newState = this.state.set('showSharingDialog', true);
    this.setState(newState);
    PlaybackActions.dialogMaskShown.defer();
  }

  /**
   * Share a FileUpload object with `fileUploadId` using `ShareSource`
   * @param  {string} fileUploadId A FileUpload.id value
   */
  onShareFileUpload([fileUpload, callback, notifyViaSlack]) {
    const state = FileUploadSharingDialogFormStore.getState();
    const data = this.shareData(fileUpload.id, state);
    if (this.isValid(data, state)) {
      setImmediate(async () => {
        await this.getInstance().create(fileUpload, {...data, notifyViaSlack});
        callback && callback();
      });
    }
  }

  onSharingDataChanged(fileUpload) {
    if (!fileUpload) {
      // A hack to force a refresh of the dialog
      setImmediate(() => {
        const state = this.state.set('shareDataError', new Map());
        this.setState(state);
      });
      return;
    }
    const state = FileUploadSharingDialogFormStore.getState();
    const data = this.shareData(fileUpload.id, state);
    this.isValid(data, state);
  }

  /**
   * Return a shareable data object that can be passed to `ShareSource.create`
   * @param fileUploadId id of the file to share.
   * @param  {immutable.Map} state `FileUploadSharingDialogFormStore.getState()`
   * @return {object} An object that can be passed to `ShareSource.create`
   */
  shareData(fileUploadId, state) {
    const sharingValues = state.get('sharingValues');
    const {emails, userIds, teamIds, departmentIds} = classifyTargets(sharingValues);
    const data = {
      userIds,
      teamIds,
      departmentIds,
      emails,
      password: state.get('password'),
    };
    if (state.get('expireAccess') && state.get('willExpireAccess')) {
      data.expiresAt = state.get('willExpireAccess');
    }
    const contentToShare = state.get('contentToShare');

    if (contentToShare && contentToShare.value === 'highlights') {
      data.tags = List(state.get('selectedHighlights'))
        .map(highlight => {
          return highlight.value;
        })
        .toArray();
    }
    if (state.get('comments') && state.get('comments').length > 0) {
      data.comments = state.get('comments');
    }
    return data;
  }

  /**
   * Return true if `data` is valid; otherwise return false and update this.state.get('shareDataError')
   * @param  {object}  data A data object that's ready to share
   * @param  {immutable.Map} state `FileUploadSharingDialogFormStore.getState()`
   * @return {Boolean} true if `data` is valid; false if not
   */
  isValid(data, state) {
    const opened = this.state.get('showSharingDialog');
    if (!opened) {
      // console.log('Getting isValid after dialog closed, some stray UI events like scrolling');
      return true;
    }
    let error = Map();
    const contentToShare = state.get('contentToShare');
    if (contentToShare) {
      if (contentToShare.value === 'fullCall' && data.tags && data.tags.length > 0) {
        error = error.set(
          'contentToShare',
          'You must select either "Full call" or "Selected highlights".'
        );
      } else if (contentToShare.value === 'highlights' && (!data.tags || data.tags.length === 0)) {
        error = error.set('tags', 'You have not selected any highlights.');
      }
    }

    if (!data.comments) {
      error = error.set('comments', 'You have not provided any reasons for sharing this call.');
    }
    if (
      data.userIds.length === 0 &&
      data.teamIds.length === 0 &&
      data.departmentIds.length === 0 &&
      data.emails.length === 0
    ) {
      error = error.set(
        'sharingTargets',
        'You have not provided any names or emails to share this call with.'
      );
    }
    if (state.get('passwordProtected') && !data.password) {
      error = error.set('password', 'This field is required');
    }
    setTimeout(() => {
      const state = this.state.set('shareDataError', error);
      this.setState(state);
    }, 0);
    return error.isEmpty();
  }

  onShareFileUploadLoading() {
    const state = this.state.set('isSharingFileUploadLoading', true);
    this.setState(state);
  }

  /**
   * Show a material-ui snackbar at the top of the page to let the user know
   * that a share has been created successfully
   */
  onShareFileUploadSuccess([fileUpload, share, response]) {
    const coachMe = FileUploadSharingDialogFormStore.getState().get('requestFeedback');
    if (coachMe) {
      setImmediate(() => {
        CoachMeSource.postCoachMe(fileUpload, true);
      });
    }
    const state = this.state.set('isSharingFileUploadLoading', false);
    this.setState(state);
    setImmediate(() => {
      SharingActions.closeSharingDialog();
      SharingFormActions.reset();
    });
    ToastStore.addToast({
      dismissible: true,
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(response.data.externalShareLink);
          ToastActions.addToast({
            message: 'Copied!',
            position: 'top-center',
          });
        } catch (e) {
          console.error(e);
          ToastActions.addToast({
            type: 'error',
            message: "Couldn't copy share link to clipboard.",
            position: 'top-center',
          });
        }
      },
      message: `Click to copy external link`,
      position: 'top-center',
    });
  }

  onShareFileUploadError(data) {
    let state = this.state.set('isSharingFileUploadLoading', false);
    this.setState(state);
  }
}

export default alt.createStore(FileUploadSharingStore, 'FileUploadSharingStore');
